import { CSSProperties, ReactNode } from "react";
import { IonPopover } from "@ionic/react";
// The parser import is to safely handle passing html in the tooltip string prop
import Parser from "html-react-parser";
import { Colors, Icon } from "main";

// Defines global styles to center the Tooltip on the page.
import "./Tooltip.css";

/********* Styles ********/
const styles = {
  tooltip: {
    "--min-width": "300px",
    "--max-width": "335px",
    "--width": "auto",
  } as CSSProperties,
  tooltipBody: {
    border: "solid 1px",
    padding: 16,
    display: "flex",
    gap: 12,
    borderRadius: 12,
  } as CSSProperties,
  tooltipContent: {
    margin: 0,
  } as CSSProperties,
  tooltipTitle: {
    padding: "0 0 12px 0",
    margin: 0,
    overflowWrap: "break-word",
    wordBreak: "break-all",
  } as CSSProperties,
  tooltipTheme: {
    color: Colors.black,
    background: Colors.purple200,
    borderColor: Colors.purple500,
  } as CSSProperties,
  dangerTheme: {
    borderColor: Colors.red500,
    backgroundColor: Colors.pink,
    color: Colors.red500,
  } as CSSProperties,
  successTheme: {
    color: Colors.green500,
    borderColor: Colors.green500,
    backgroundColor: Colors.green200,
  } as CSSProperties,
  infoTheme: {
    color: Colors.white200,
    borderColor: Colors.blue600,
    backgroundColor: Colors.blue600,
  } as CSSProperties,
};

const themeColors: Record<Theme, keyof typeof Colors> = {
  tooltip: "purple500",
  info: "white200",
  danger: "red500",
  success: "green500",
};

/********* Types ********/
type Theme = "tooltip" | "info" | "danger" | "success";

type TooltipProps = {
  /** Trigger element `id` attribute */
  trigger: string;
  header?: string;
  content: string | ReactNode;
  theme?: Theme;
  /**
   * @default false
   */
  disableIcon?: boolean;
  positionAlign?: Parameters<typeof IonPopover>[0]["alignment"] | "page-center";
};

/***** Components *****/
export function Tooltip(props: TooltipProps) {
  const { trigger, header, content, theme = "tooltip", disableIcon = false, positionAlign } = props;

  return (
    <IonPopover
      trigger={trigger}
      mode="md"
      style={styles.tooltip}
      // Adds the `placement-page-center` classname to position in the center of
      // the page. This style is defined in `Tooltip.css`.
      className={positionAlign === "page-center" ? "placement-page-center" : ""}
      // If the `center` position is defined, do not pass it to IonPopover since
      // it not a Ionic defined placement. This is a custom placement style that
      // we have defined.
      alignment={positionAlign === "page-center" ? undefined : positionAlign}
      // TODO: Not configurable currently, expose if needed.
      animated={positionAlign === "page-center" ? false : true}
    >
      <div style={{ ...styles.tooltipBody, ...styles[`${theme}Theme`] }}>
        {!disableIcon && <Icon name="Info" color={Colors[themeColors[theme]] ?? Colors.black} />}
        <div>
          {header && <h3 style={styles.tooltipTitle}>{header}</h3>}
          <p style={styles.tooltipContent}>
            {typeof content === "string" ? Parser(content) : content}
          </p>
        </div>
      </div>
    </IonPopover>
  );
}
